import api from './api';

// Types aligned with public-events API (pg_eventos table)
export interface EventNew {
  id: number;
  titulo: string;
  slug: string;
  descricao_curta?: string;
  descricao?: string;
  categoria: string;
  tipo?: string;
  local_nome: string;
  local_cidade: string;
  local_endereco?: string;
  local_pais?: string;
  local_capacidade?: number;
  data_inicio: string;
  data_fim?: string;
  data_abertura_portas?: string;
  imagem_capa?: string;
  imagem_banner?: string;
  galeria?: string[];
  tags?: string[];
  status: string;
  preco_minimo?: number;
  preco_maximo?: number;
  tipos_bilhetes?: number;
  bilhetes_disponiveis?: number;
  total_bilhetes_vendidos?: number;
  organizador_nome?: string;
  organizador_entidade?: string;
  // Detail fields
  tipos_bilhete?: TicketType[];
  estatisticas?: EventStats;
}

export interface TicketType {
  id: number;
  nome: string;
  descricao?: string;
  preco: number;
  preco_original?: number;
  quantidade_total: number;
  quantidade_vendida: number;
  quantidade_disponivel: number;
  min_por_compra: number;
  max_por_compra: number;
  beneficios?: string[];
  zona_acesso?: string;
  status: string;
  venda_inicio?: string | null;
  venda_fim?: string | null;
  // Estado calculado pelo servidor: o bilhete mostra-se sempre, mas só se compra
  // quando compravel === true. estado_venda ∈ EM_BREVE|ABERTO|ENCERRADO|ESGOTADO.
  estado_venda?: 'EM_BREVE' | 'ABERTO' | 'ENCERRADO' | 'ESGOTADO';
  compravel?: boolean;
}

export interface EventStats {
  total_vendidos: number;
  total_checkins: number;
  receita_total: number;
  percentagem_vendida: number;
}

export interface EventCategory {
  categoria: string;
  total: number;
}

export interface EventCity {
  cidade: string;
  total: number;
}

class EventsService {
  /**
   * GET /public-events — List published events
   */
  async getEvents(params?: {
    page?: number;
    limit?: number;
    category?: string;
    city?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ data: EventNew[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.category) queryParams.append('category', params.category);
    if (params?.city) queryParams.append('city', params.city);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);

    const query = queryParams.toString();
    const response = await api.get<{
      success: boolean;
      data?: EventNew[];
      pagination?: any;
      error?: string;
    }>(`/public-events${query ? `?${query}` : ''}`);

    if (response.data.success) {
      return {
        data: response.data.data || [],
        pagination: response.data.pagination || {},
      };
    }
    throw new Error(response.data.error || 'Falha ao carregar eventos');
  }

  /**
   * GET /public-events/featured
   */
  async getFeaturedEvents(): Promise<EventNew[]> {
    const response = await api.get<{
      success: boolean;
      data?: EventNew[];
      error?: string;
    }>('/public-events/featured');

    if (response.data.success) return response.data.data || [];
    return [];
  }

  /**
   * GET /public-events/upcoming
   */
  async getUpcomingEvents(days?: number): Promise<EventNew[]> {
    const response = await api.get<{
      success: boolean;
      data?: EventNew[];
      error?: string;
    }>(`/public-events/upcoming${days ? `?days=${days}` : ''}`);

    if (response.data.success) return response.data.data || [];
    return [];
  }

  /**
   * GET /public-events/categories
   */
  async getCategories(): Promise<EventCategory[]> {
    const response = await api.get<{
      success: boolean;
      data?: Array<{ categoria: string; total_eventos?: number; total?: number; eventos_futuros?: number }>;
    }>('/public-events/categories');

    if (response.data.success && response.data.data) {
      return response.data.data.map(c => ({
        categoria: c.categoria,
        total: c.total_eventos ?? c.total ?? 0,
      }));
    }
    return [];
  }

  /**
   * GET /public-events/cities
   */
  async getCities(): Promise<EventCity[]> {
    const response = await api.get<{
      success: boolean;
      data?: EventCity[];
    }>('/public-events/cities');

    if (response.data.success) return response.data.data || [];
    return [];
  }

  /**
   * GET /public-events/:id — Full event detail
   */
  async getEventDetail(idOrSlug: string | number): Promise<EventNew> {
    const response = await api.get<{
      success: boolean;
      data?: EventNew;
      error?: string;
    }>(`/public-events/${idOrSlug}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Evento não encontrado');
  }

  /**
   * GET /public-events/:id/ticket-types
   */
  async getTicketTypes(eventId: number | string): Promise<TicketType[]> {
    const response = await api.get<{
      success: boolean;
      data?: TicketType[];
    }>(`/public-events/${eventId}/ticket-types`);

    if (response.data.success) return response.data.data || [];
    return [];
  }

  /**
   * POST /public-events/:id/checkout
   *
   * Envia uma chave de idempotência, para um retry não criar segunda compra.
   *
   * O ponto delicado é a chave ter de ser ESTÁVEL entre tentativas. Gerar um
   * UUID novo a cada chamada não serviria de nada: cada retry levaria chave
   * diferente e o servidor criaria pedidos diferentes. Por isso a chave é
   * guardada em sessionStorage, indexada pela intenção de compra — evento,
   * bilhetes e email do comprador. Enquanto a intenção for a mesma, a chave é a
   * mesma; depois de o pedido ficar criado, é descartada, para uma segunda compra
   * igual e legítima ser tratada como compra nova.
   *
   * A lógica vive aqui, no serviço, e não nos ecrãs: assim qualquer chamador
   * fica protegido sem ter de saber disto.
   */
  async checkout(eventId: number | string, data: {
    items: { ticket_type_id: number; quantidade: number }[];
    comprador_nome: string;
    comprador_email: string;
    comprador_telefone?: string;
    metodo_pagamento: string;
    codigo_promocional?: string;
  }): Promise<any> {
    const intencao = 'pagali:idem:' + JSON.stringify({
      e: String(eventId),
      i: (data.items || []).map((x) => `${x.ticket_type_id}x${x.quantidade}`).sort(),
      m: data.comprador_email || '',
    });

    let chave: string | null = null;
    try {
      chave = sessionStorage.getItem(intencao);
      if (!chave) {
        chave = (crypto as any)?.randomUUID?.()
          || `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem(intencao, chave!);
      }
    } catch {
      // Modo privado ou storage cheio: segue sem chave, como antes.
      chave = null;
    }

    const response = await api.post<{
      success: boolean;
      data?: any;
      error?: string;
      repetido?: boolean;
    }>(`/public-events/${eventId}/checkout`, chave ? { ...data, idempotency_key: chave } : data);

    if (response.data.success && response.data.data) {
      // Pedido criado (ou recuperado): a intenção está cumprida e a chave sai.
      try { if (chave) sessionStorage.removeItem(intencao); } catch { /* ignore */ }
      return response.data.data;
    }
    throw new Error(response.data.error || 'Erro no checkout');
  }

  /**
   * GET /user-events/orders/:id
   * Obtém o estado actual do pedido (para polling após SISP).
   */
  async getOrder(orderId: number | string): Promise<any> {
    const response = await api.get<{ success: boolean; data?: any; error?: string }>(
      `/user-events/orders/${orderId}`
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Pedido não encontrado');
  }

  /**
   * POST /user-events/orders/:id/pay
   * Paga um pedido existente via wallet ou SISP.
   */
  /**
   * Paga um pedido de bilhetes.
   *
   * TOKEN e o cartao guardado (tokenizacao SISP): cobra na hora, sem reintroduzir dados,
   * e exige token_id. Faltava aqui e no events.cv — quem tinha cartao guardado tinha de
   * o reintroduzir em cada compra de bilhete, que e a razao de o ter guardado.
   *
   * A rota do backend confirma que o token pertence ao cliente autenticado antes de
   * cobrar; nao e este cliente que o garante.
   */
  async payOrder(
    orderId: number | string,
    paymentMethod: 'WALLET' | 'SISP' | 'TOKEN',
    extras?: { pin?: string; returnUrl?: string; token_id?: string }
  ): Promise<any> {
    const response = await api.post<{
      success: boolean;
      data?: any;
      error?: string;
    }>(`/user-events/orders/${orderId}/pay`, {
      metodo_pagamento: paymentMethod,
      ...extras,
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Erro no pagamento');
  }

  /**
   * POST /public-events/:id/validate-promo
   */
  async validatePromo(eventId: number | string, code: string, subtotal: number): Promise<any> {
    const response = await api.post<{
      success: boolean;
      data?: any;
      error?: string;
    }>(`/public-events/${eventId}/validate-promo`, { code, subtotal });

    if (response.data.success) return response.data.data;
    throw new Error(response.data.error || 'Código inválido');
  }
}

export default new EventsService();
